export type PageSlice = { start: number; end: number; repeatHeader: boolean };

export interface PaginationInput {
  contentEnd: number;
  pageHeight: number;
  continuationTopSpace: number;
  pageNumberSpace: number;
  tableBodyStart: number;
  tableEnd: number;
  headerHeight: number;
  rowBreaks: number[];
  safeBreaks?: number[];
  finalBlock?: { start: number; end: number } | null;
}

export interface PaginationResult {
  slices: PageSlice[];
  oversizedFinalBlock: boolean;
}

export const calculatePageSlices = ({
  contentEnd,
  pageHeight,
  continuationTopSpace,
  pageNumberSpace,
  tableBodyStart,
  tableEnd,
  headerHeight,
  rowBreaks,
  safeBreaks = [],
  finalBlock = null,
}: PaginationInput): PaginationResult => {
  const endOfContent = Math.max(0, Math.round(contentEnd));
  const finalHeight = finalBlock ? finalBlock.end - finalBlock.start : 0;
  const continuationCapacity = pageHeight - continuationTopSpace - pageNumberSpace;
  const oversizedFinalBlock = finalHeight > continuationCapacity;
  const breaks = [...new Set([
    tableBodyStart,
    ...rowBreaks,
    ...safeBreaks,
    ...(finalBlock ? [finalBlock.start, finalBlock.end] : []),
    endOfContent,
  ].filter((point) => Number.isFinite(point) && point > 0 && point <= endOfContent))].sort((a, b) => a - b);

  const slices: PageSlice[] = [];
  let start = 0;
  let guard = 0;
  while (start < endOfContent && guard < 100) {
    guard += 1;
    const repeatHeader = start >= tableBodyStart && start < tableEnd && headerHeight > 0;
    const topSpace = start > 0 ? continuationTopSpace : 0;
    const availableHeight = Math.max(1, pageHeight - topSpace - pageNumberSpace - (repeatHeader ? headerHeight : 0));
    const limit = Math.min(endOfContent, start + availableHeight);
    let end = limit;

    if (limit < endOfContent) {
      const mustMoveFinalBlock = finalBlock
        && !oversizedFinalBlock
        && start < finalBlock.start
        && finalBlock.start < limit
        && finalBlock.end > limit;
      if (mustMoveFinalBlock) {
        end = finalBlock.start;
      } else {
        const candidates = breaks.filter((point) => point > start && point <= limit);
        if (candidates.length) end = candidates[candidates.length - 1];
      }
    }

    if (end <= start) end = Math.min(endOfContent, start + availableHeight);
    slices.push({ start, end, repeatHeader });
    start = end;
  }

  return { slices, oversizedFinalBlock };
};
